import React from 'react';
import { ArrowLeft, ArrowRight, BookOpen, Code, AlertCircle, Bell, Rocket, Zap, PlayCircle } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BackToTop from '../components/BackToTop';

const bgImage = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/8jj8xvut_FOR%20TECHNICAL%20DOCUMENTATION.png';

const startHereSteps = [
  {
    n: 1,
    icon: BookOpen,
    eyebrow: 'Understand',
    title: 'API Overview',
    description: 'Read architecture, request/response fundamentals and notification methods. ~3 min.',
    link: '/technical-documentation/api-reference/overview',
  },
  {
    n: 2,
    icon: Rocket,
    eyebrow: 'Integrate',
    title: 'Hosted Payments Page',
    description: 'The fastest path to your first transaction — no PCI scope, 3D Secure handled for you.',
    link: '/technical-documentation/api-reference/hosted-payment-page',
    recommended: true,
  },
  {
    n: 3,
    icon: PlayCircle,
    eyebrow: 'Validate',
    title: 'Request Reference',
    description: 'Reference XML request and response samples for every endpoint.',
    link: '/technical-documentation/api-reference/hosted-payment-page#request-reference',
  },
];

const guides = [
  {
    icon: Code,
    title: 'API Reference',
    description: 'Complete API endpoint documentation with request/response examples, parameters and error codes.',
    link: '/technical-documentation/api-reference',
  },
  {
    icon: Bell,
    title: 'Notifications',
    description: 'Configure and manage payment notifications, webhooks and real-time transaction alerts for your integration.',
    link: '/technical-documentation/notifications',
  },
  {
    icon: BookOpen,
    title: 'Appendix',
    description: 'Supplementary reference materials including response codes, currency codes, country codes and additional technical specifications.',
    link: '/technical-documentation/appendix',
  },
  {
    icon: AlertCircle,
    title: 'Troubleshooting',
    description: 'Common issues, error resolution guides and FAQs to help you quickly resolve any technical challenges.',
    link: '/technical-documentation/troubleshooting',
  },
];

const TechnicalDocumentation = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col" data-testid="technical-docs-page">
      <Header />

      <div className="h-20" />

      {/* Full-page background image */}
      <div className="relative flex-1">
        <div
          className="fixed inset-0 top-20 bg-cover bg-center"
          style={{ backgroundImage: `url(${bgImage})` }}
        />
        <div className="fixed inset-0 top-20 bg-white/0" />

        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-[#1F4E79] hover:text-[#163a5f] font-medium mb-8 transition-colors duration-200"
            data-testid="tech-docs-back-button"
          >
            <ArrowLeft size={18} />
            Back to Home
          </button>

          {/* Intro */}
          <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-12 mb-8">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-3" data-testid="tech-docs-title">
              Technical Documentation
            </h1>
            <p className="text-lg text-gray-600 font-bold mb-6">
              Comprehensive guides and resources for developers and technical teams.
            </p>

            <div className="space-y-4 text-gray-700 text-base sm:text-lg leading-relaxed">
              <p>
                Welcome to our Technical Documentation — your complete resource for integrating and managing our payment solutions. Whether you're a developer building your first integration or a technical team optimising existing systems, you'll find detailed guidance, code examples and best practices to support your implementation.
              </p>

              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 pt-2">What You'll Find</h2>
              <p>
                Our technical documentation covers everything from initial setup to advanced configuration options. With step-by-step guides, API references, and troubleshooting resources, we've designed these materials to make your integration process as smooth as possible.
              </p>
            </div>
          </div>

          {/* Start Here onboarding pathway */}
          <section
            className="bg-gradient-to-br from-[#1F4E79] to-[#163a5f] rounded-lg shadow-xl p-8 sm:p-10 mb-8 relative overflow-hidden"
            aria-label="Start here onboarding pathway"
            data-testid="start-here-pathway"
          >
            {/* decorative accent */}
            <div className="absolute -top-6 -right-6 w-32 h-32 bg-[#ef5b25]/20 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-8 -left-8 w-40 h-40 bg-white/5 rounded-full blur-3xl pointer-events-none" />

            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <Zap size={16} className="text-[#ef5b25]" strokeWidth={2.5} />
                <p className="text-[11px] font-bold text-[#ef5b25] uppercase tracking-[0.15em]">Start Here</p>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2" data-testid="start-here-title">
                From zero to your first transaction — a guided walkthrough.
              </h2>
              <p className="text-white/75 text-sm sm:text-base leading-relaxed mb-7 max-w-3xl">
                Follow these three steps in order. No local setup required — everything runs from the docs.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {startHereSteps.map((s, i) => {
                  const Icon = s.icon;
                  return (
                    <Link
                      key={s.n}
                      to={s.link}
                      className="group relative rounded-lg bg-white/10 hover:bg-white/15 border border-white/15 hover:border-white/30 backdrop-blur-sm p-5 transition-all duration-200 hover:-translate-y-0.5"
                      data-testid={`start-here-step-${s.n}`}
                    >
                      {s.recommended && (
                        <span
                          className="absolute -top-2.5 -right-2.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider text-white shadow-md bg-gradient-to-r from-[#ef5b25] to-[#f58220]"
                          data-testid="start-here-recommended"
                        >
                          Fastest
                        </span>
                      )}
                      <div className="flex items-center gap-3 mb-3">
                        <span className="inline-flex items-center justify-center w-8 h-8 bg-[#ef5b25] text-white rounded-full text-sm font-bold shadow-md">
                          {s.n}
                        </span>
                        <div className="flex items-center gap-1.5 text-white/60">
                          <Icon size={14} />
                          <span className="text-[10px] font-semibold uppercase tracking-wider">{s.eyebrow}</span>
                        </div>
                      </div>
                      <h3 className="text-base font-bold text-white mb-1 group-hover:text-[#ef5b25] transition-colors">
                        {s.title}
                      </h3>
                      <p className="text-xs text-white/70 leading-relaxed mb-4">{s.description}</p>
                      <span className="inline-flex items-center gap-1 text-[#ef5b25] font-semibold text-xs group-hover:gap-2 transition-all">
                        Go to step {s.n} <ArrowRight size={13} />
                      </span>
                      {i < startHereSteps.length - 1 && (
                        <span className="hidden md:block absolute -right-2 top-1/2 -translate-y-1/2 text-white/30 text-2xl font-bold select-none pointer-events-none">&rsaquo;</span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Getting Started callout */}
          <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3" data-testid="tech-docs-getting-started">
              Getting Started
            </h2>
            <p className="text-gray-700 text-base sm:text-lg leading-relaxed">
              New to our APIs? Start with our guides and work through the examples at your own pace. Experienced developers can use our comprehensive API reference for detailed implementation details.
            </p>
          </div>

          {/* Guide Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
            {guides.map((guide, index) => {
              const Icon = guide.icon;
              return (
                <Link
                  key={index}
                  to={guide.link}
                  className="group bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 hover:shadow-xl hover:bg-white/90 transition-all duration-300"
                  data-testid={`tech-docs-guide-${index}`}
                >
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#1F4E79] transition-colors duration-200 flex items-center gap-2">
                    <Icon size={24} className="text-[#1F4E79] flex-shrink-0" />
                    {guide.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">
                    {guide.description}
                  </p>
                  <span className="inline-flex items-center gap-1 text-[#1F4E79] font-semibold text-sm group-hover:gap-2 transition-all duration-200">
                    Learn more <ArrowRight size={16} />
                  </span>
                </Link>
              );
            })}
          </div>

          {/* Need Help */}
          <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-12 mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" data-testid="tech-docs-need-help">
              Need Help?
            </h2>
            <p className="text-gray-700 text-base sm:text-lg leading-relaxed mb-8">
              Whether you're facing a specific coding challenge, need clarification on our API responses or require guidance on best practices, our team combines deep technical knowledge with practical implementation experience to get you back on track quickly.
            </p>

            <div className="space-y-4">
              <div>
                <p className="font-semibold text-gray-900">Phone Support</p>
                <p className="text-gray-700">EU: +353 (0)46 901 1288</p>
                <p className="text-gray-700">UK: +44 (0)20 7183 5253</p>
              </div>
              <div>
                <p className="font-semibold text-gray-900">Email</p>
                <a
                  href="mailto:csr@ecomm365.com"
                  className="text-[#1F4E79] hover:text-[#163a5f] underline transition-colors duration-200"
                  data-testid="tech-docs-email-link"
                >
                  csr@ecomm365.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-10">
        <Footer />
      </div>
      <BackToTop />
    </div>
  );
};

export default TechnicalDocumentation;
